import React from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useWorkingModel } from "@/hooks/useWorkingModel";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader, RequireModel } from "@/components/PageParts";
import DeliverableState from "@/components/DeliverableState";
import { Button, Card } from "@/components/ui";

export default function Takeoff() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  const [wm] = useWorkingModel();

  const { data, loading, error } = useDeliverable(
    () => api.editorTakeoff({ model: wm.model, floors: wm.floors || 1, wall_height_m: 3.2, slab_system: wm.slab_system, design_code: wm.design_code }),
    [wm?.title, wm?.design_code],
    !!wm?.model
  );

  const rows = data?.table || [];
  const sections = [...new Set(rows.map((r) => r.section))];

  const exp = async (fmt) => {
    try {
      await api.editorTakeoffExport(fmt, { model: wm.model, floors: wm.floors || 1, wall_height_m: 3.2, slab_system: wm.slab_system, project_name: wm.title });
    } catch (e) {
      toast.error(String(e.message || e));
    }
  };

  return (
    <RequireModel>
      {() => (
        <div className="mx-auto max-w-5xl px-4 py-6">
          <PageHeader
            eyebrow={isAr ? "الحصر" : "Take-off"}
            title={isAr ? "حصر الكميات" : "Quantity take-off"}
            subtitle={isAr ? "كميات الجدران والخرسانة والحديد - بدون أسعار." : "Wall, concrete and rebar quantities - no money figures."}
            right={
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => exp("xlsx")} data-testid="takeoff-xlsx"><FileSpreadsheet size={15} /> XLSX</Button>
                <Button size="sm" variant="outline" onClick={() => exp("pdf")} data-testid="takeoff-pdf"><FileText size={15} /> PDF</Button>
              </div>
            }
          />
          <DeliverableState loading={loading} error={error} empty={!rows.length} isAr={isAr}>
            <div className="space-y-5">
              {sections.map((sec) => (
                <Card key={sec} className="p-0 overflow-hidden">
                  <div className="border-b border-border bg-white/5 px-4 py-2 text-sm font-black">{sec}</div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-xs text-muted-foreground">
                        <th className="px-4 py-2 text-start">{isAr ? "البند" : "Item"}</th>
                        <th className="px-4 py-2 text-end">{isAr ? "الكمية" : "Qty"}</th>
                        <th className="px-4 py-2 text-end">{isAr ? "الوحدة" : "Unit"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.filter((r) => r.section === sec).map((r, i) => (
                        <tr key={i} className="border-b border-border/50 last:border-0">
                          <td className="px-4 py-2">{r.item}</td>
                          <td className="px-4 py-2 text-end font-bold">{Number(r.qty).toLocaleString()}</td>
                          <td className="px-4 py-2 text-end text-muted-foreground">{r.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              ))}
            </div>
          </DeliverableState>
        </div>
      )}
    </RequireModel>
  );
}
